// AUT-32 (web-admin), généralisé par AUT-33 (web-parent) — fait transiter un jeton
// d'invitation à travers `/register` et `/login`, pour qu'un invité soit renvoyé sur
// SA page d'acceptation juste après s'être authentifié, sans rouvrir le lien reçu
// par email.
//
// Transport : paramètres d'URL, JAMAIS `sessionStorage`/`localStorage` — le jeton
// est déjà dans l'URL du lien reçu par email (aucune exposition supplémentaire),
// et un paramètre d'URL disparaît avec la navigation : un jeton oublié ne peut pas
// déclencher une acceptation lors d'une inscription ultérieure sans rapport.
//
// Chaque app déclare ses TYPES d'invitation et la page d'acceptation de chacun
// (`acceptPaths`) : le paramètre `invitation` de l'URL ne choisit qu'une clé de
// cette liste fermée, jamais un chemin — aucune redirection ouverte possible. Un
// type inconnu est ignoré (parcours `/register`/`/login` standard).

export const INVITATION_TOKEN_PARAM = 'invitationToken';
export const INVITATION_KIND_PARAM = 'invitation';

export interface InvitationContinuation<K extends string> {
  kind: K;
  token: string;
}

export interface InvitationContinuationConfig<K extends string> {
  /** Page d'acceptation de chaque type d'invitation de l'app. */
  acceptPaths: Record<K, string>;
  /**
   * Type implicite quand l'URL ne porte pas `invitation=` — et omis de l'URL pour
   * ce type-là. Permet à une app n'ayant qu'un type (web-admin) de garder des URLs
   * `?invitationToken=…` seules.
   */
  defaultKind?: K;
}

export interface InvitationContinuationHelpers<K extends string> {
  /** Lit la continuation portée par l'URL courante, ou `null` (parcours standard). */
  read: (searchParams: URLSearchParams) => InvitationContinuation<K> | null;
  /** `/register` ou `/login`, avec la continuation propagée s'il y en a une. */
  authPath: (path: '/register' | '/login', continuation: InvitationContinuation<K> | null) => string;
  /** Page d'acceptation à rejoindre une fois authentifié, jeton compris. */
  acceptPath: (continuation: InvitationContinuation<K>) => string;
}

export function createInvitationContinuation<K extends string>(
  config: InvitationContinuationConfig<K>,
): InvitationContinuationHelpers<K> {
  const isKind = (value: string): value is K => Object.prototype.hasOwnProperty.call(config.acceptPaths, value);

  return {
    read(searchParams) {
      const token = searchParams.get(INVITATION_TOKEN_PARAM);
      if (!token) return null;
      const rawKind = searchParams.get(INVITATION_KIND_PARAM) ?? config.defaultKind;
      if (rawKind === undefined || !isKind(rawKind)) return null;
      return { kind: rawKind, token };
    },
    authPath(path, continuation) {
      if (!continuation) return path;
      const params = new URLSearchParams();
      if (continuation.kind !== config.defaultKind) params.set(INVITATION_KIND_PARAM, continuation.kind);
      params.set(INVITATION_TOKEN_PARAM, continuation.token);
      return `${path}?${params.toString()}`;
    },
    acceptPath(continuation) {
      return `${config.acceptPaths[continuation.kind]}?token=${encodeURIComponent(continuation.token)}`;
    },
  };
}

// Posé dans `location.state` en revenant sur la page d'acceptation depuis
// `/register`/`/login` : sur succès, la page redirige alors directement vers
// l'accueil. Absent (lien email ouvert en étant déjà connecté), l'écran « Invitation
// acceptée » reste inchangé.
export interface InvitationContinuationState {
  continueAfterAuth: true;
}

export const INVITATION_CONTINUATION_STATE: InvitationContinuationState = { continueAfterAuth: true };

export function isInvitationContinuation(state: unknown): boolean {
  return (state as Partial<InvitationContinuationState> | null)?.continueAfterAuth === true;
}
